// client/src/pages/admin/adminCourseManagement.jsx

import React, { useState, useEffect, useContext } from 'react';
import { AdminCourseDetailModal, ContentListModal, LessonModal, AssessmentModal } from '../../modals';
import { useNavigate, useParams } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import BackButton from '../../components/customs/backButton';
// import LabGradingRubric from '../../modals/labEngine/labGradingRubric';
import showNotification from '../../utilities/showNotification';
import { GlobalContext } from '../../context/provider';
import {
  useCourse, 
  useCategory,
  useInstructor, 
  useBlobStorage, 
  useStudent, 
  useVisualCircuit
} from '../../hooks';

import { 
    formatDate, 
    formatShortDate, 
    formatLongDate, 
    formatRelativeTime,
    formatDateTime } from '../../utilities/dateUtils';

import '../../styles/globalstyles.css';
import './adminCourseManagement.css';

const AdminCourseManagement = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { authState: { authData } } = useContext(GlobalContext);
  
  const {
    getAllCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    publishCourse,
    unpublishCourse,
    updateCourseContent,
    addLesson,
    addLab,
    addQuiz,
    addExam,
    getCourseById,
  } = useCourse();

  const { triggerDownload } = useBlobStorage();

  const {
    categories,
    loading: categoriesLoading,
    getAllCategories,
    createCategory,
    updateCategory,
    getCategoryByTitle,
    deleteCategory: deleteCategoryAPI
  } = useCategory();

  const { 
    getCourseStudents,
    getCourseLabs,
    getCourseQuizzes,
    getCourseExams,
    getCourseCapstones,
  } = useInstructor();

  const { 
    getQuizProgress,
    getExamProgress,
    getLabProgress,
    getCapstoneProgress
  } = useStudent();

  const { courseCircuits, getCircuitsByCourse } = useVisualCircuit();

  // Tab Management
  const [activeTab, setActiveTab] = useState('courses');

  // Loading & Data
  const [isLoading, setIsLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [domainName, setDomainName] = useState('');
  
  // Course Management
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedListType, setSelectedListType] = useState(null);
  const [uploadingContentFiles, setUploadingContentFiles] = useState(false);
      
  // Category Management
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tags, setTags] = useState([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryViewMode, setCategoryViewMode] = useState('list'); // 'list' or 'courses'
  const [categoryCourses, setCategoryCourses] = useState([]);
  
  // Modals & Forms
  const [modalVisible, setModalVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [contentFormVisible, setContentFormVisible] = useState(false);
  const [categoryFormVisible, setCategoryFormVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [categoryAlertVisible, setCategoryAlertVisible] = useState(false);
  const [courseInfo, setCourseInfo] = useState(null);
  const [tempWeight, setTempWeight] = useState('');
  const [editingWeight, setEditingWeight] = useState(null); 

  const [showCouponForm, setShowCouponForm] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    couponCode: '',
    discountType: 'percentage',
    discountValue: 0,
    minPurchaseAmount: null,
    maxUses: null,
    expiryDate: null,
    description: '',
    isActive: true
  });
  const [editingCouponIndex, setEditingCouponIndex] = useState(null);

  const [formData, setFormData] = useState({
    courseId: '',
    title: '',
    shortDescription: '',
    longDescription: '',
    category: '',
    level: '',
    price: '',
    currency: '',
    duration: '',
    credits: '',
    prerequisites: '',
    allowInstallments: true,
    installmentPlans: ['monthly_3', 'monthly_6', 'monthly_12'],
    refundPolicy: 'standard_30',
    coupons: []  
  });
  const [contentType, setContentType] = useState('');
  const [contentData, setContentData] = useState({});
  const [categoryFormData, setCategoryFormData] = useState({ 
    title: '', 
    tags: [], 
    tagInput: '',
    categoryId: '' 
  });
  const [students, setStudents] = useState([]);

  // Add these after your existing state declarations
  const [showListModal, setShowListModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  // existing state declarations
  const [studentsProgress, setStudentsProgress] = useState({});
  const [statsAssignments, setStatsAssignments] = useState({
    labsPending: 0, labsSubmitted: 0,
    quizzesPending: 0, quizzesSubmitted: 0,
    examsPending: 0, examsSubmitted: 0,
    capstonesPending: 0, capstonesSubmitted: 0,
    allPending: 0, allSubmitted: 0,
  });
  // Grading data
  const [labSubmissions, setLabSubmissions] = useState([]);
  const [quizSubmissions, setQuizSubmissions] = useState([]);
  const [examSubmissions, setExamSubmissions] = useState([]);
  const [capstoneSubmissions, setCapstoneSubmissions] = useState([]);
  
  useEffect(() => {
    // Load data on initial mount
    loadPage();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadPage();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadPage = async () => {
    setIsLoading(true);
    await Promise.all([loadCourses(), loadCategories()]);
    setIsLoading(false);
  };

  const loadCourses = async () => {
    try {
      const response = await getAllCourses();
      if (response.success) {
        setCourses(response.data || []);
      }
    } catch (error) {
      showNotification('Failed to load courses', 'error');
    }
  };

  const loadCategories = async () => {
    try {
      await getAllCategories();
    } catch (error) {
      showNotification('Failed to load categories', 'error');
    }
  };

  const handleCreateCategory = () => {
    setCategoryFormData({
      categoryId: '',
      title: '',
      tags: [],
      tagInput: ''
    });
    setCategoryFormVisible(true);
  };

  const handleCreateCourse = () => {
    setFormData({
      courseId: '',
      title: '',
      shortDescription: '',
      longDescription: '',
      category: '',
      passingGrade: 60,
      level: '',
      price: '',
      currency: '',
      duration: '',
      credits: '',
      prerequisites: '',
      learningOutcomes: '',
      allowInstallments: true,
      installmentPlans: ['monthly_3', 'monthly_6', 'monthly_12'],
      refundPolicy: 'standard_30',
      coupons: []  // ← A
    });
    setFormVisible(true);
  };

  const handleEditCourse = (course) => {
    setFormData({
      courseId: course.courseId,
      title: course.title || '',
      shortDescription: course.shortDescription || '',
      longDescription: course.longDescription || '',
      category: course.category || '',
      passingGrade: course.passingGrade || 60,
      level: course.level || '',
      price: course.price || '',
      currency: course.currency || '',
      duration: course.duration || '',
      credits: course.credits || '',
      prerequisites: course.prerequisites?.join(', ') || '',
      learningOutcomes: course.learningOutcomes?.join(', ') || '',
      allowInstallments: course.paymentOptions?.allowInstallments ?? true,
      installmentPlans: course.paymentOptions?.installmentPlans || ['monthly_3', 'monthly_6', 'monthly_12'],
      refundPolicy: course.paymentOptions?.refundPolicy || 'standard_30',
      coupons: course.paymentOptions?.coupons || [],
      createdBy: authData?.id
    });
    setFormVisible(true);
    setModalVisible(false);
  };

  const handleSubmitCourse = async () => {
    // Validation
    if (!formData.title || !formData.category || !formData.level || !formData.price || !formData.currency) {
      alert('Please fill in all required fields');
      return;
    }

    const courseData = {
      courseId: formData.courseId || undefined,
      title: formData.title,
      shortDescription: formData.shortDescription,
      longDescription: formData.longDescription,
      category: formData.category,
      level: formData.level,
      price: formData.price,
      currency: formData.currency,
      duration: formData.duration,
      credits: formData.credits,
      prerequisites: formData.prerequisites ? formData.prerequisites.split(',').map(p => p.trim()) : [],
      passingGrade: formData.passingGrade || 60,
      // Payment Options with Coupons
      paymentOptions: {
        allowInstallments: formData.allowInstallments,
        installmentPlans: formData.installmentPlans,
        refundPolicy: formData.refundPolicy,
        coupons: formData.coupons || []
      }
    };

    console.log('📦 Sending courseData:', courseData);
  console.log('🎫 Coupons being sent:', formData.coupons);

    try {
      let response;
      if (selectedCourse && selectedCourse.courseId) {
        // Update existing course
        response = await updateCourse(selectedCourse.courseId, courseData);
      } else {
        // Create new course
        response = await createCourse(formData.courseId || `CRS${Date.now()}`, courseData);
      }
      console.log('✅ Response from backend:', response);
      if (response.success) {
        showNotification(
          selectedCourse ? 'Course updated successfully' : 'Course created successfully',
          'success'
        );
        setFormVisible(false);
        loadPage(); // Reload courses
      } else {
        showNotification(response.message || 'Failed to save course', 'error');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      showNotification('Failed to save course', 'error');
    }
  };

  const handleDeleteCourse = async () => {
    try {
      const response = await deleteCourse(selectedCourse.courseId);
      if (response.success) {
        showNotification('Course deleted successfully', 'success');
        setAlertVisible(false);
        setModalVisible(false);
        loadCourses();
      } else {
        showNotification(response.message || 'Failed to delete course', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete course', 'error');
    }
  };

  const handleTogglePublish = async (course) => {
    console.log("toggle publish course: ", course.courseId, authData.id);
    
    try {
      const response = course.isPublished  
        ? await unpublishCourse(course.courseId, authData?.id)
        : await publishCourse(course.courseId, authData?.id);
      
      if (response.success) {
        showNotification(`Course ${course.isPublished ? 'unpublished' : 'published'} successfully`, 'success');
        loadCourses();
      } else {
        showNotification(response.message || 'Failed to update course status', 'error');
      }
    } catch (error) {
      showNotification('Failed to update course status', 'error');
    }
  };

  const handleDuplicateCourse = async (course) => {
    try {
      // Generate new course ID
      const newCourseId = 'CRS' + Date.now().toString().slice(-6);
      
      // Create duplicate course data
      const duplicateCourseData = {
        title: `${course.title} (Copy)`,
        shortDescription: course.shortDescription,
        longDescription: course.longDescription,
        category: course.category,
        level: course.level,
        price: course.price,
        currency: course.currency,
        duration: course.duration,
        credits: course.credits,
        createdBy: authData?.id || authData?.name || 'Admin',
        prerequisites: course.prerequisites || [],
        learningOutcomes: course.learningOutcomes || [],
        // Copy all content arrays
        lessons: course.lessons || [],
        labs: course.labs || [],
        quizzes: course.quizzes || [],
        exams: course.exams || [],
        capstone: course.capstone,
        circuits: course.circuits || []
      };

      const response = await createCourse(newCourseId, duplicateCourseData);

      if (response.success) {
        showNotification(`Course duplicated successfully as ${newCourseId}`, 'success');
        setModalVisible(false);
        loadCourses();
      } else {
        showNotification(response.message || 'Failed to duplicate course', 'error');
      }
    } catch (error) {
      showNotification('Failed to duplicate course', 'error');
    }
  };

  const handleSubmitContent = async () => {
    try {
      if (!contentData.title) {
        showNotification('Please enter a title', 'error');
        return;
      }

      const contentId = contentType.toUpperCase() + Date.now().toString().slice(-6);
      let response;

      if (contentType === 'lesson') {
        response = await addLesson(contentData.courseId, contentId, contentData);
      } else if (contentType === 'lab') {
        response = await addLab(contentData.courseId, contentId, contentData);
      } else if (contentType === 'quiz') {
        response = await addQuiz(contentData.courseId, contentId, contentData);
      } else if (contentType === 'exam') {
        response = await addExam(contentData.courseId, contentId, contentData);
      }

      if (response.success) {
        showNotification(`${contentType} added successfully`, 'success');
        setContentFormVisible(false);
      } else {
        showNotification(response.message || `Failed to add ${contentType}`, 'error');
      }
    } catch (error) {
      showNotification(`Failed to add ${contentType}`, 'error');
    }
  };

  const handleSubmitCategory = async () => {
    try {
      if (!categoryFormData.title.trim()) {
        showNotification('Please enter a category title', 'error');
        return;
      }

      let response;
      if (categoryFormData.categoryId) {
        // Edit existing category
        response = await updateCategory(categoryFormData.categoryId, {
          title: categoryFormData.title,
          tags: categoryFormData.tags
        });
      } else {
        // Create new category
        response = await createCategory({
          title: categoryFormData.title,
          tags: categoryFormData.tags
        });
      }

      if (response.success) {
        showNotification(`Category ${categoryFormData.categoryId ? 'updated' : 'created'} successfully`, 'success');
        setCategoryFormVisible(false);
        await getAllCategories();
      } else {
        showNotification(response.message || 'Failed to save category', 'error');
      }
    } catch (error) {
      showNotification('Failed to save category', 'error');
    }
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setCategoryFormData({
      categoryId: category.categoryId,
      title: category.title,
      tags: category.tags || [],
      tagInput: ''
    });
    setCategoryFormVisible(true);
    setCategoryModalVisible(false);
  };

  const handleDeleteCategory = async () => {
    try {
      const response = await deleteCategoryAPI(selectedCategory.categoryId, {
        id: authData?.id,
        name: authData?.name
      });
      
      if (response.success) {
        showNotification('Category deleted successfully', 'success');
        setCategoryAlertVisible(false);
        setCategoryModalVisible(false);
        await getAllCategories();
      } else {
        showNotification(response.message || 'Failed to delete category', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete category', 'error');
    }
  };

  const handleViewCourse = async (course) => {
    setSelectedCourse(course);

    if (course?.category) {
      const response = await getCategoryByTitle(course.category);
      if (response?.success && response.data?.tags) {
        setTags(response.data.tags);
      }
    }
    
    try {
      // Load course info
      const courseResponse = await getCourseById(course.courseId);
      setDomainName(courseResponse.data.category);
      console.log("Course Info: ", courseResponse);
      if (courseResponse.success) {
        setCourseInfo(courseResponse.data);
      }

      if (course.courseId && authData?.id) {
        await getCircuitsByCourse(course.courseId, authData.id);
      }

      // Load students
      const studentsResponse = await getCourseStudents(authData.id, course.courseId);
      const courseStudents = studentsResponse?.data?.students || [];
      if (studentsResponse.success) {
        setStudents(courseStudents);
      }

      // Get all submissions
      const [labs, quizzes, exams, capstones] = await Promise.all([
        getCourseLabs(authData.id, course.courseId),
        getCourseQuizzes(authData.id, course.courseId),
        getCourseExams(authData.id, course.courseId),
        getCourseCapstones(authData.id, course.courseId)
      ]);

      // Process submissions for stats (copy the rest of the code from loadPage)
      const labsData = labs?.data || [];
      const quizzesData = quizzes?.data || [];
      const examsData = exams?.data || [];
      const capstonesData = capstones?.data || [];

      const labsPending = labsData.filter(s => s.status === 'submitted').length;
      const quizzesPending = quizzesData.filter(s => s.status === 'submitted').length;
      const examsPending = examsData.filter(s => s.status === 'submitted').length;
      const capstonesPending = capstonesData.filter(s => s.status === 'submitted').length;
      const totalPending = labsPending + quizzesPending + examsPending + capstonesPending;

      const labsGraded = labsData.filter(s => s.status === 'graded').length;
      const quizzesGraded = quizzesData.filter(s => s.status === 'graded').length;
      const examsGraded = examsData.filter(s => s.status === 'graded').length;
      const capstonesGraded = capstonesData.filter(s => s.status === 'graded').length;
      const totalGraded = labsGraded + quizzesGraded + examsGraded + capstonesGraded;

      const labsAll = labsGraded + labsPending;
      const examsAll = examsGraded + examsPending;
      const quizzesAll = quizzesGraded + quizzesPending;
      const capstonesAll = capstonesGraded + capstonesPending;
      const totalAll = totalGraded + totalPending;

      setStatsAssignments({
        labsPending: labsPending, labsSubmitted: labsAll,
        quizzesPending: quizzesPending, quizzesSubmitted: quizzesAll,
        examsPending: examsPending, examsSubmitted: examsAll,
        capstonesPending: capstonesPending, capstonesSubmitted: capstonesAll,
        allPending: totalPending, allSubmitted: totalAll 
      });

      setLabSubmissions(labs?.data || []);
      setQuizSubmissions(quizzes?.data || []);
      setExamSubmissions(exams?.data || []);
      setCapstoneSubmissions(capstones?.data || []);

      // Load student progress
      const progressData = {};
      for (const student of courseStudents) {
        const loginId = student.loginId || student.studentId;
        try {
          const [labProgressRes, quizProgressRes, examProgressRes, capstoneProgressRes] = await Promise.all([
            getLabProgress(loginId, course.courseId),
            getQuizProgress(loginId, course.courseId),
            getExamProgress(loginId, course.courseId),
            getCapstoneProgress(loginId, course.courseId)
          ]);

          progressData[loginId] = {
            labProgress: labProgressRes.success ? labProgressRes.data : { courseId: course.courseId, labs: [] },
            quizProgress: quizProgressRes.success ? quizProgressRes.data : { courseId: course.courseId, quizzes: [] },
            examProgress: examProgressRes.success ? examProgressRes.data : { courseId: course.courseId, exams: [] },
            capstoneProjects: capstoneProgressRes.success && capstoneProgressRes.data 
              ? capstoneProgressRes.data 
              : { courseId: course.courseId, capstones: [] },
            totalLabs: labProgressRes.data?.labs?.length || 0,
            totalQuizzes: quizProgressRes.data?.quizzes?.length || 0,
            totalExams: examProgressRes.data?.exams?.length || 0,
            totalCapstones: capstoneProgressRes.data?.capstones?.length || 0
          };
        } catch (error) {
          console.error(`Failed to load progress for student ${loginId}:`, error);
          progressData[loginId] = {
            labProgress: { courseId: course.courseId, labs: [] },
            quizProgress: { courseId: course.courseId, quizzes: [] },
            examProgress: { courseId: course.courseId, exams: [] },
            capstoneProjects: { courseId: course.courseId, capstones: [] },
            totalLabs: 0,
            totalQuizzes: 0,
            totalExams: 0,
            totalCapstones: 0
          };
        }
      }

      setStudentsProgress(progressData);
      console.log("Students progress loaded:", progressData);

    } catch (error) {
      console.error('Failed to load course data:', error);
      showNotification('Failed to load course data', 'error');
    }
    
    setModalVisible(true);
  };

  const closeContentModal = () => {
    setShowContentModal(false);
    setSelectedContentType(null);
    setSelectedItemId(null);
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      await triggerDownload(fileId, filename, authData.id);
      showNotification('File download started', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download file', 'error');
    }
  };

  const [hasTextbook, setHasTextbook] = useState(false);
  
  useEffect(() => {
      if (!selectedCourse?.category) {
          setHasTextbook(false);
          return;
      }
      setHasTextbook(false);
      const url = `/courses/${encodeURIComponent(selectedCourse.category)}/textbook.json`;
      console.log('Checking textbook at:', url);
      fetch(url)
          .then(res => {
              console.log('Textbook response:', res.ok, res.status, res.headers.get('content-type'));
              const contentType = res.headers.get('content-type');
              setHasTextbook(res.ok && contentType?.includes('application/json'));
          })
          .catch((err) => {
              console.log('Textbook fetch error:', err);
              setHasTextbook(false);
          });
  }, [selectedCourse?.category]);

  const calculateTotalWeight = () => {
    if (!courseInfo) return 0;
    
    let total = 0;
    
    // Sum lab weights
    courseInfo.labs?.forEach(lab => {
      total += (lab.gradeWeight || 0) * 100;
    });
    
    // Sum quiz weights
    courseInfo.quizzes?.forEach(quiz => {
      total += (quiz.gradeWeight || 0) * 100;
    });
    
    // Sum exam weights
    courseInfo.exams?.forEach(exam => {
      total += (exam.gradeWeight || 0) * 100;
    });
    
    // Add capstone weight
    if (courseInfo.capstone) {
      total += (courseInfo.capstone.gradeWeight || 0) * 100;
    }
    
    return Math.round(total * 100) / 100; // Round to 1 decimal place
  };

  // Start editing weight
  const handleWeightEdit = (type, id, currentWeight) => {
    setEditingWeight({ type, id });
    setTempWeight((currentWeight * 100).toString()); // Convert to percentage
  };

  // Cancel weight edit
  const handleWeightCancel = () => {
    setEditingWeight(null);
    setTempWeight('');
  };

  // Save weight edit
  const handleWeightSave = async () => {
    if (!editingWeight) return;
    
    const { type, id } = editingWeight;
    const newWeight = parseFloat(tempWeight) / 100; // Convert from percentage to decimal
    
    if (isNaN(newWeight) || newWeight < 0 || newWeight > 1) {
      showNotification('Invalid weight. Must be between 0 and 100%', 'error');
      return;
    }
    
    try {
      const contentTypeMap = {
        'lab': 'labs',
        'quiz': 'quizzes',
        'exam': 'exams',
        'capstone': 'capstone'
      };
      
      const idFieldMap = {
        'lab': 'labId',
        'quiz': 'quizId',
        'exam': 'examId',
        'capstone': 'capstoneId'
      };
      
      const response = await updateCourseContent(
        selectedCourse.courseId,
        type,
        id,
        { gradeWeight: newWeight, updatedBy: authData.id }
      );
      
      if (response.success) {
        showNotification('Grade weight updated successfully', 'success');
        setEditingWeight(null);
        setTempWeight('');
        await loadPage(); // Reload to get updated data
      } else {
        showNotification('Failed to update grade weight', 'error');
      }
    } catch (error) {
      console.error('Error updating grade weight:', error);
      showNotification('An error occurred', 'error');
    }
  };

  // Handle Add Coupon
  const handleAddCoupon = () => {
    // Validation
    if (!newCoupon.couponCode || !newCoupon.couponCode.trim()) {
      alert('Please enter a coupon code');
      return;
    }

    if (!newCoupon.discountValue || newCoupon.discountValue <= 0) {
      alert('Please enter a valid discount value');
      return;
    }

    if (newCoupon.discountType === 'percentage' && newCoupon.discountValue > 100) {
      alert('Percentage discount cannot exceed 100%');
      return;
    }

    // Check for duplicate coupon code
    const isDuplicate = (formData.coupons || []).some(
      (c, idx) => c.couponCode === newCoupon.couponCode.toUpperCase() && idx !== editingCouponIndex
    );

    if (isDuplicate) {
      alert('Coupon code already exists');
      return;
    }

    // Prepare coupon data
    const couponData = {
      couponCode: newCoupon.couponCode.toUpperCase(),
      description: newCoupon.description,
      discountType: newCoupon.discountType,
      discountValue: parseFloat(newCoupon.discountValue),
      minPurchaseAmount: newCoupon.minPurchaseAmount ? parseFloat(newCoupon.minPurchaseAmount) : undefined,
      maxUses: newCoupon.maxUses ? parseInt(newCoupon.maxUses) : undefined,
      expiryDate: newCoupon.expiryDate ? new Date(newCoupon.expiryDate) : undefined,
      isActive: newCoupon.isActive !== false,
      usedCount: 0,
      createdAt: new Date()
    };

    // Add or update coupon
    if (editingCouponIndex !== null) {
      // Update existing coupon
      const updatedCoupons = [...(formData.coupons || [])];
      updatedCoupons[editingCouponIndex] = {
        ...updatedCoupons[editingCouponIndex],
        ...couponData
      };
      setFormData({ ...formData, coupons: updatedCoupons });
      setEditingCouponIndex(null);
    } else {
      // Add new coupon
      setFormData({
        ...formData,
        coupons: [...(formData.coupons || []), couponData]
      });
    }

    // Reset form
    setNewCoupon({
      couponCode: '',
      discountType: 'percentage',
      discountValue: 0,
      minPurchaseAmount: null,
      maxUses: null,
      expiryDate: null,
      description: '',
      isActive: true
    });
    setShowCouponForm(false);

    showNotification('Coupon saved successfully', 'success');
  };

  // Handle Edit Coupon
  const handleEditCoupon = (index) => {
    const coupon = formData.coupons[index];
    setNewCoupon({
      couponCode: coupon.couponCode,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minPurchaseAmount: coupon.minPurchaseAmount || null,
      maxUses: coupon.maxUses || null,
      expiryDate: coupon.expiryDate ? new Date(coupon.expiryDate).toISOString().split('T')[0] : null,
      description: coupon.description || '',
      isActive: coupon.isActive !== false
    });
    setEditingCouponIndex(index);
    setShowCouponForm(true);
  };

  // Handle Delete Coupon
  const handleDeleteCoupon = (index) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    const updatedCoupons = formData.coupons.filter((_, idx) => idx !== index);
    setFormData({ ...formData, coupons: updatedCoupons });
    showNotification('Coupon deleted successfully', 'success');
  };

  const getCourseCountForCategory = (categoryName) => {
    return courses.filter(c => c.category === categoryName).length;
  };

  const getCategoryTags = (categoryName) => {
    const category = categories.find(cat => cat.title === categoryName);
    return category?.tags || [];
  };

  // Replace the openContentModal function with this:
  const openListModal = (contentType) => {
    setSelectedListType(contentType);
    setShowListModal(true);
  };

  const closeListModal = () => {
    setShowListModal(false);
    setSelectedListType(null);
  };

  const openContentModalFromList = (contentType, itemId) => {
    setSelectedContentType(contentType);
    setSelectedItemId(itemId);
    setShowContentModal(true);
    setShowListModal(false);
  };

  // ============================================================================
  // FILTERING
  // ============================================================================

  const filteredCourses = courses.filter(course => {
    const matchesSearch = !searchQuery || 
      course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.courseId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = !filterCategory || course.category === filterCategory;
    return matchesSearch && matchesFilter;
  });

  // ============================================================================
  // HEADER DATA
  // ============================================================================
  const profileHeaderData = { 
      ...authData, 
      // title: '📚 Course Management',
      // subTitle: `${courseInfo?.courseId}`
  };

  return (
    <div className="main-container">
      <div className="main-content">
        
        {/* Header */}
        <div className="header-row">
          <ProfileHeader 
            data={profileHeaderData} 
            activePage="admin-courses" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>

        <div className="page-title">
          <h3>📚 Course Management</h3>
          {/* <p>{labInfo?.title || courseInfo?.courseId || 'Circuit Design'}</p> */}
        </div>

        <BackButton 
          to="/admin/dashboard"
          activeTab="management"
          label="←"
        />

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <button 
            className={`tab-button ${activeTab === 'courses' ? 'active-tab' : ''}`}
            onClick={() => {
              setActiveTab('courses');
              setCategoryViewMode('list');
            }}
          >
            📚 Courses
          </button>
          <button 
            className={`tab-button ${activeTab === 'categories' ? 'active-tab' : ''}`}
            onClick={() => {
              setActiveTab('categories');
              setCategoryViewMode('list');
            }}
          >
            🏷️ Categories
          </button>
        </div>

        {/* ====================================================================== */}
        {/* COURSES TAB */}
        {/* ====================================================================== */}
        {activeTab === 'courses' && (
          <>
            {/* Compact Search & Filter Bar */}
            <div className="search-filter-bar">
              <input
                type="text"
                placeholder="🔍 Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-filter-input"
              />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="search-filter-select"
              >
                <option value="">📂 All Categories</option>
                {categories.map((cat, idx) => (
                  <option key={idx} value={cat.title}>{cat.title}</option>
                ))}
              </select>
              <button
                onClick={handleCreateCourse}
                className="compact-btn compact-btn-primary"
              >
                ➕ Create Course
              </button>
            </div>

            {/* Courses List */}
            <h3>📚 All Courses - Quick Access</h3>
            {isLoading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Loading courses...</p>
              </div>
            ) : (
              <div className="scrollable-list">
                {filteredCourses.length > 0 ? (
                  <div className="activity-list" style={{width: '90%'}}>
                    {filteredCourses.slice(0, 5).map((course) => {
                      
                      return (
                        <div key={course.courseId} className="activity-item">
                          <span className="activity-icon">📚</span>
                          <div className="activity-details" style={{ flex: 1 }}>
                            <p><strong>{course.title}</strong></p>
                            <p className="expertise">
                              {course.category || 'General'} •
                              {` ${course.level}` || ' All Levels'} • 
                              {course.credits ? ` ${course.credits} Credits` : ' Self-paced'}
                            </p>
                          </div>
                          {/* action buttons */}
                          <div className="course-card-actions">
                            <CustomButton 
                              buttonTitle="✏️ View/Edit"
                              action={() => handleViewCourse(course)}
                              bgColor="#667eea"
                            />
                          </div>
                        </div>
                      );
                    })}
                    {filteredCourses.length > 5 && (
                      <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <CustomButton 
                          buttonTitle="View All Courses"
                          action={() => setActiveTab('my-courses')}
                          bgColor="#3b82f6"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>No courses enrolled yet 📚</p>
                    <CustomButton 
                      buttonTitle="Explore Courses"
                      action={() => setActiveTab('explore')}
                      bgColor="#667eea"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ====================================================================== */}
        {/* CATEGORIES TAB */}
        {/* ====================================================================== */}
        {activeTab === 'categories' && (
          <>
            {/* Compact Search & Action Bar */}
            <div className="search-filter-bar">
              <input
                type="text"
                placeholder="🔍 Search categories..."
                value={categorySearchQuery}
                onChange={(e) => setCategorySearchQuery(e.target.value)}
                className="search-filter-input"
              />
              <button
                onClick={handleCreateCategory}
                className="compact-btn compact-btn-primary"
              >
                ➕ Create Category
              </button>
            </div>

            {/* Categories Grid */}
            {isLoading || categoriesLoading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Loading categories...</p>
              </div>
            ) : (
              categories.length > 0 ? (
                <div className="scrollable-list">
                  {categories
                    .filter(category => 
                      !categorySearchQuery || 
                      category.title?.toLowerCase().includes(categorySearchQuery.toLowerCase()) ||
                      category.tags?.some(tag => tag.toLowerCase().includes(categorySearchQuery.toLowerCase()))
                    )
                    .map((category, idx) => {
                      const courseCount = getCourseCountForCategory(category.title);
                      return (
                        <div
                          key={idx}
                          className="general-small-card"
                          onClick={() => handleViewCategory(category)}
                        >
                          <div className="badge-container">
                            <div className="new-badge">{courseCount} COURSES</div>
                          </div>
                          <div className="general-small-card-info">
                            <div className="category-card-icon">
                              🏷️
                            </div>
                            <h3>{category.title}</h3>
                            {category.tags?.length > 0 ? (
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '6px', 
                                marginTop: '8px' 
                              }}>
                                {category.tags.map((tag, tagIdx) => (
                                  <span
                                    key={tagIdx}
                                    className="cateogry-tags"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="category-card-description">No tags</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="no-items">
                  <p>No categories found 🏷️</p>
                  <CustomButton 
                    buttonTitle="Create First Category"
                    action={handleCreateCategory}
                    bgColor="#10b981"
                  />
                </div>
              )
            )}
          </>
        )}

        {/* ====================================================================== */}
        {/* ADMIN COURSE CONTENT MODAL */}
        {/* ====================================================================== */}
        <AdminCourseDetailModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          course={selectedCourse}
          tags={tags}
          hasTextbook={hasTextbook}
          courseCanvases={courseCircuits} 
          editingWeight={editingWeight}
          tempWeight={tempWeight}
          onTempWeightChange={setTempWeight}
          onWeightEdit={handleWeightEdit}
          onWeightSave={handleWeightSave}
          onWeightCancel={handleWeightCancel}
          calculateTotalWeight={calculateTotalWeight}
          onEditCourse={handleEditCourse}
          onTogglePublish={handleTogglePublish}
          onDuplicateCourse={handleDuplicateCourse}
          onDeleteCourse={() => setAlertVisible(true)}
          onOpenListModal={openListModal}
          onNavigateCanvases={() => navigate(`/labengine/course/${selectedCourse.courseId}/canvases`)}
        />
        
        <ContentListModal
          visible={showListModal && !!selectedListType}
          onClose={closeListModal}
          role="admin"
          contentType={selectedListType}
          items={selectedCourse?.[selectedListType] || []}
          onView={(itemId) => openContentModalFromList(selectedListType, itemId)}
        />

        {/* ADMIN - LESSON VIEW MODAL */}
        <LessonModal
          visible={showContentModal && selectedContentType === "lessons"}
          onClose={() => setShowContentModal(false)}
          role="admin"
          lesson={
            selectedCourse?.lessons?.find(l => l.lessonId === selectedItemId)
          }
        />

        {/* LESSON CONTENT MODAL */}
        <LessonModal
          visible={showContentModal && selectedContentType === 'lessons'}
          onClose={closeContentModal}
          role="admin"
          lesson={(() => {
            const items = courseInfo?.lessons;
            return items?.find(l => l.lessonId === selectedItemId) || null;
          })()}
          isEditMode={false}
          onDownload={handleDownloadAttachment}
          isUploadingFiles={uploadingContentFiles}
        />

        {/* ADMIN ASSESSMENT CONTENT View/Edit MODAL — labs / quizzes / exams */}
        <AssessmentModal
          visible={showContentModal && (
            selectedContentType === 'labs' ||
            selectedContentType === 'quizzes' ||
            selectedContentType === 'exams'
          )}
          authData={authData}
          courseId={courseId}
          domain={domainName}
          role="admin"
          phase="overview"
          assessmentType={
            selectedContentType === 'labs' ? 'lab' :
            selectedContentType === 'quizzes' ? 'quiz' : 'exam'
          }
          content={(() => {
            if (!selectedContentType || !selectedItemId) return null;
            const items = courseInfo?.[selectedContentType];
            const idField = selectedContentType === 'labs' ? 'labId' :
              selectedContentType === 'quizzes' ? 'quizId' : 'examId';
            return items?.find(i => i[idField] === selectedItemId) || null;
          })()}
          onLoad={loadPage}
          onClose={closeContentModal}
          onDownload={handleDownloadAttachment}
          // labGradingRubric={LabGradingRubric}
        />

        {/* ====================================================================== */}
        {/* COMPLETE COURSE FORM MODAL WITH COUPON MANAGEMENT */}
        {/* ====================================================================== */}
        {formVisible && (
          <div className="modal-overlay">
            <div className="modal-content-top">
              <div className="modal-header-top">
                <div className="modal-info-group">
                  <div className="modal-header-title">
                    <h3>{formData?.courseId ? '✏️ Edit Course' : '➕ Create New Course'}</h3>
                  </div>
                </div>
                <div className="modal-actions-group">
                  <button className="modal-close-btn" onClick={() => setFormVisible(false)}>
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">
                <div className="course-form-container">
                  
                  {/* ========== SECTION 1: BASIC INFORMATION ========== */}
                  <div className="form-section">
                    <div className="section-header">
                      <h4>📋 Basic Information</h4>
                    </div>
                    <div className="section-content">
                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Course ID</strong> <span className="optional">(Optional)</span></label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData?.courseId || ''}
                            onChange={(e) => setFormData({...formData, courseId: e.target.value})}
                            placeholder="e.g., CRS001 (Leave empty to auto-generate)"
                            disabled={!!formData?.courseId && formData?.courseId.startsWith('CRS')}
                          />
                          <small className="field-hint">
                            Leave empty to auto-generate, or enter a custom ID
                          </small>
                        </div>
                      </div>

                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Course Title</strong> <span className="required">*</span></label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            placeholder="e.g., IoT Fundamentals"
                          />
                        </div>
                      </div>

                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Short Description</strong></label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.shortDescription}
                            onChange={(e) => setFormData({...formData, shortDescription: e.target.value})}
                            placeholder="Brief course description (max 100 characters)"
                            maxLength={100}
                          />
                          <small className="field-hint">
                            {formData.shortDescription?.length || 0}/100 characters
                          </small>
                        </div>
                      </div>

                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Detailed Description</strong></label>
                          <textarea
                            className="form-textarea"
                            value={formData.longDescription}
                            onChange={(e) => setFormData({...formData, longDescription: e.target.value})}
                            placeholder="Comprehensive course description"
                            maxLength={500}
                            rows={4}
                          />
                          <small className="field-hint">
                            {formData.longDescription?.length || 0}/500 characters
                          </small>
                        </div>
                      </div>

                      <div className="admincourse-form-row two-columns">
                        <div className="admincourse-form-field">
                          <label><strong>Category</strong> <span className="required">*</span></label>
                          <select
                            className="form-select"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat, idx) => (
                              <option key={idx} value={cat.title}>{cat.title}</option>
                            ))}
                          </select>
                        </div>

                        <div className="admincourse-form-field">
                          <label><strong>Level</strong> <span className="required">*</span></label>
                          <select
                            className="form-select"
                            value={formData.level}
                            onChange={(e) => setFormData({...formData, level: e.target.value})}
                          >
                            <option value="">Select Level</option>
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Advanced">Advanced</option>
                          </select>
                        </div>
                      </div>

                      <div className="admincourse-form-row two-columns">
                        <div className="admincourse-form-field">
                          <label><strong>Duration</strong></label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.duration}
                            onChange={(e) => setFormData({...formData, duration: e.target.value})}
                            placeholder="e.g., 8 weeks"
                          />
                        </div>

                        <div className="admincourse-form-field">
                          <label><strong>Credits</strong></label>
                          <input
                            type="number"
                            className="form-input"
                            value={formData.credits}
                            onChange={(e) => setFormData({...formData, credits: e.target.value})}
                            placeholder="e.g., 3"
                          />
                        </div>
                      </div>

                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Prerequisites</strong></label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.prerequisites}
                            onChange={(e) => setFormData({...formData, prerequisites: e.target.value})}
                            placeholder="e.g., Basic Programming, Electronics (comma-separated)"
                          />
                        </div>
                      </div>

                      <div className="admincourse-form-row two-columns">
                        <div className="admincourse-form-field">
                          <label><strong>Passing Grade (%)</strong> <span className="required">*</span></label>
                          <input
                            type="number"
                            className="form-input"
                            value={formData.passingGrade || 60}
                            onChange={(e) => setFormData({...formData, passingGrade: parseFloat(e.target.value)})}
                            placeholder="e.g., 60"
                            min="0"
                            max="100"
                            step="1"
                          />
                          <small className="field-hint">Minimum grade required to pass this course</small>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* ========== SECTION 2: PRICING ========== */}
                  <div className="form-section">
                    <div className="section-header">
                      <h4>💰 Pricing</h4>
                    </div>
                    <div className="section-content">
                      <div className="admincourse-form-row two-columns">
                        <div className="admincourse-form-field">
                          <label><strong>Price</strong> <span className="required">*</span></label>
                          <input
                            type="number"
                            className="form-input"
                            value={formData.price}
                            onChange={(e) => setFormData({...formData, price: e.target.value})}
                            placeholder="99.00"
                            step="0.01"
                            min="0"
                          />
                        </div>

                        <div className="admincourse-form-field">
                          <label><strong>Currency</strong> <span className="required">*</span></label>
                          <select
                            className="form-select"
                            value={formData.currency}
                            onChange={(e) => setFormData({...formData, currency: e.target.value})}
                          >
                            <option value="">Select Currency</option>
                            <option value="CAD">🇨🇦 CAD</option>
                            <option value="USD">🇺🇸 USD</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ========== SECTION 3: PAYMENT OPTIONS ========== */}
                  <div className="form-section">
                    <div className="section-header">
                      <h4>💳 Payment Options</h4>
                    </div>
                    <div className="section-content">
                      
                      {/* Allow Installments Checkbox */}
                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.allowInstallments}
                              onChange={(e) => setFormData({...formData, allowInstallments: e.target.checked})}
                            />
                            <strong>Allow Installment Payments</strong>
                          </label>
                          <small className="field-hint">
                            Students can pay for this course in monthly installments
                          </small>
                        </div>
                      </div>

                      {/* Installment Plans */}
                      {formData.allowInstallments && (
                        <div className="admincourse-form-row">
                          <div className="admincourse-form-field full-width">
                            <label><strong>Available Installment Plans</strong></label>
                            <div className="checkbox-group">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={formData.installmentPlans?.includes('monthly_3')}
                                  onChange={(e) => {
                                    const plans = e.target.checked
                                      ? [...(formData.installmentPlans || []), 'monthly_3']
                                      : (formData.installmentPlans || []).filter(p => p !== 'monthly_3');
                                    setFormData({...formData, installmentPlans: plans});
                                  }}
                                />
                                3 Monthly Payments
                                {formData.price && (
                                  <span className="plan-amount">
                                    ({formData.currency} {(parseFloat(formData.price) / 3*1.05).toFixed(2)}/month)
                                  </span>
                                )}
                              </label>

                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={formData.installmentPlans?.includes('monthly_6')}
                                  onChange={(e) => {
                                    const plans = e.target.checked
                                      ? [...(formData.installmentPlans || []), 'monthly_6']
                                      : (formData.installmentPlans || []).filter(p => p !== 'monthly_6');
                                    setFormData({...formData, installmentPlans: plans});
                                  }}
                                />
                                6 Monthly Payments
                                {formData.price && (
                                  <span className="plan-amount">
                                    ({formData.currency} {(parseFloat(formData.price) / 6*1.15).toFixed(2)}/month)
                                  </span>
                                )}
                              </label>

                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={formData.installmentPlans?.includes('monthly_12')}
                                  onChange={(e) => {
                                    const plans = e.target.checked
                                      ? [...(formData.installmentPlans || []), 'monthly_12']
                                      : (formData.installmentPlans || []).filter(p => p !== 'monthly_12');
                                    setFormData({...formData, installmentPlans: plans});
                                  }}
                                />
                                12 Monthly Payments
                                {formData.price && (
                                  <span className="plan-amount">
                                    ({formData.currency} {(parseFloat(formData.price) / 12*1.25).toFixed(2)}/month)
                                  </span>
                                )}
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Refund Policy */}
                      <div className="admincourse-form-row">
                        <div className="admincourse-form-field full-width">
                          <label><strong>Refund Policy</strong> <span className="required">*</span></label>
                          <select
                            className="form-select"
                            value={formData.refundPolicy}
                            onChange={(e) => setFormData({...formData, refundPolicy: e.target.value})}
                          >
                            <option value="standard_30">Standard (30 days)</option>
                            <option value="extended_60">Extended (60 days)</option>
                            <option value="no_refund">No Refunds</option>
                          </select>
                          <small className="field-hint policy-hint">
                            {formData.refundPolicy === 'standard_30' && (
                              <span className="hint-success">✓ Students can request refund within 30 days of purchase</span>
                            )}
                            {formData.refundPolicy === 'extended_60' && (
                              <span className="hint-success">✓ Students can request refund within 60 days of purchase</span>
                            )}
                            {formData.refundPolicy === 'no_refund' && (
                              <span className="hint-warning">⚠️ This course is non-refundable after purchase</span>
                            )}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ========== SECTION 4: COUPON MANAGEMENT ========== */}
                  <div className="form-section">
                    <div className="section-header">
                      <h4>🎫 Discount Coupons</h4>
                      <button
                        type="button"
                        className="btn-add-coupon"
                        onClick={() => setShowCouponForm(!showCouponForm)}
                      >
                        {showCouponForm ? '− Cancel' : '+ Add Coupon'}
                      </button>
                    </div>
                    <div className="section-content">
                      
                      {/* Add Coupon Form */}
                      {showCouponForm && (
                        <div className="coupon-form">
                          <div className="admincourse-form-row two-columns">
                            <div className="admincourse-form-field">
                              <label><strong>Coupon Code</strong> <span className="required">*</span></label>
                              <input
                                type="text"
                                className="form-input"
                                value={newCoupon.couponCode}
                                onChange={(e) => setNewCoupon({...newCoupon, couponCode: e.target.value.toUpperCase()})}
                                placeholder="e.g., SAVE20"
                                style={{ textTransform: 'uppercase' }}
                              />
                            </div>

                            <div className="admincourse-form-field">
                              <label><strong>Discount Type</strong> <span className="required">*</span></label>
                              <select
                                className="form-select"
                                value={newCoupon.discountType}
                                onChange={(e) => setNewCoupon({...newCoupon, discountType: e.target.value})}
                              >
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed Amount</option>
                              </select>
                            </div>
                          </div>

                          <div className="admincourse-form-row two-columns">
                            <div className="admincourse-form-field">
                              <label>
                                <strong>Discount Value</strong> <span className="required">*</span>
                              </label>
                              <input
                                type="number"
                                className="form-input"
                                value={newCoupon.discountValue}
                                onChange={(e) => setNewCoupon({...newCoupon, discountValue: parseFloat(e.target.value)})}
                                placeholder={newCoupon.discountType === 'percentage' ? '20' : '10.00'}
                                step="0.01"
                                min="0"
                              />
                              <small className="field-hint">
                                {newCoupon.discountType === 'percentage' 
                                  ? `${newCoupon.discountValue || 0}% off` 
                                  : `${formData.currency} ${newCoupon.discountValue || 0} off`}
                              </small>
                            </div>

                            <div className="admincourse-form-field">
                              <label><strong>Min Purchase</strong></label>
                              <input
                                type="number"
                                className="form-input"
                                value={newCoupon.minPurchaseAmount || ''}
                                onChange={(e) => setNewCoupon({...newCoupon, minPurchaseAmount: parseFloat(e.target.value)})}
                                placeholder="Optional"
                                step="0.01"
                                min="0"
                              />
                              <small className="field-hint">Minimum cart value required</small>
                            </div>
                          </div>

                          <div className="admincourse-form-row two-columns">
                            <div className="admincourse-form-field">
                              <label><strong>Max Uses</strong></label>
                              <input
                                type="number"
                                className="form-input"
                                value={newCoupon.maxUses || ''}
                                onChange={(e) => setNewCoupon({...newCoupon, maxUses: parseInt(e.target.value)})}
                                placeholder="Unlimited"
                                min="1"
                              />
                              <small className="field-hint">Leave empty for unlimited</small>
                            </div>

                            <div className="admincourse-form-field">
                              <label><strong>Expiry Date</strong></label>
                              <input
                                type="date"
                                className="form-input"
                                value={newCoupon.expiryDate || ''}
                                onChange={(e) => setNewCoupon({...newCoupon, expiryDate: e.target.value})}
                              />
                              <small className="field-hint">Leave empty for no expiry</small>
                            </div>
                          </div>

                          <div className="admincourse-form-row">
                            <div className="admincourse-form-field full-width">
                              <label><strong>Description</strong></label>
                              <input
                                type="text"
                                className="form-input"
                                value={newCoupon.description || ''}
                                onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                                placeholder="e.g., Early bird discount for new students"
                              />
                            </div>
                          </div>

                          <div className="admincourse-form-row">
                            <div className="admincourse-form-field full-width">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={newCoupon.isActive !== false}
                                  onChange={(e) => setNewCoupon({...newCoupon, isActive: e.target.checked})}
                                />
                                <strong>Active</strong>
                              </label>
                              <small className="field-hint">Inactive coupons cannot be used</small>
                            </div>
                          </div>

                          <div className="coupon-form-actions">
                            <button
                              type="button"
                              className="btn-save-coupon"
                              onClick={handleAddCoupon}
                            >
                              ✓ Save Coupon
                            </button>
                            <button
                              type="button"
                              className="btn-cancel-coupon"
                              onClick={() => {
                                setShowCouponForm(false);
                                setNewCoupon({
                                  couponCode: '',
                                  discountType: 'percentage',
                                  discountValue: 0,
                                  isActive: true
                                });
                              }}
                            >
                              ✕ Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing Coupons List */}
                      {formData.coupons && formData.coupons.length > 0 ? (
                        <div className="coupons-list">
                          <h5>Existing Coupons ({formData.coupons.length})</h5>
                          {formData.coupons.map((coupon, index) => (
                            <div key={index} className={`coupon-card ${!coupon.isActive ? 'inactive' : ''}`}>
                              
                              {/* Coupon Header */}
                              <div className="coupon-header">
                                <div className="coupon-code-badge">
                                  {coupon.couponCode}
                                </div>
                                <div className="coupon-status">
                                  {coupon.isActive ? (
                                    <span className="status-active">✓ Active</span>
                                  ) : (
                                    <span className="status-inactive">✕ Inactive</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Coupon Details */}
                              <div className="coupon-details">
                                
                                {/* Discount Display */}
                                <div className="coupon-discount">
                                  <div>
                                    <div className="discount-label">Discount</div>
                                    <div className="discount-value">
                                      {coupon.discountType === 'percentage' 
                                        ? `${coupon.discountValue}%`
                                        : `${formData.currency || '$'} ${coupon.discountValue}`}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Description */}
                                {coupon.description && (
                                  <div className="coupon-description">
                                    💬 {coupon.description}
                                  </div>
                                )}
                                
                                {/* Metadata Grid */}
                                <div className="coupon-meta">
                                  {/* Min Purchase */}
                                  {coupon.minPurchaseAmount && (
                                    <div className="meta-item">
                                      <strong>Min Purchase</strong>
                                      <span>{formData.currency || '$'} {coupon.minPurchaseAmount}</span>
                                    </div>
                                  )}
                                  
                                  {/* Usage Stats */}
                                  <div className="meta-item">
                                    <strong>Usage</strong>
                                    <span>
                                      {coupon.usedCount || 0}
                                      {coupon.maxUses ? ` / ${coupon.maxUses}` : ' (Unlimited)'}
                                    </span>
                                  </div>
                                  
                                  {/* Expiry Date */}
                                  {coupon.expiryDate && (
                                    <div className="meta-item">
                                      <strong>Expires</strong>
                                      <span>
                                        {new Date(coupon.expiryDate).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Created Date */}
                                  {coupon.createdAt && (
                                    <div className="meta-item">
                                      <strong>Created</strong>
                                      <span>
                                        {new Date(coupon.createdAt).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Coupon Actions */}
                              <div className="coupon-actions">
                                <button
                                  type="button"
                                  className="btn-edit-coupon"
                                  onClick={() => handleEditCoupon(index)}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn-delete-coupon"
                                  onClick={() => handleDeleteCoupon(index)}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-coupons">
                          <p>🎫 No coupons created yet. Click "Add Coupon" to create your first discount code.</p>
                        </div>
                      )}

                    </div>
                  </div>

                </div>

                {/* Form Actions */}
                <div className="form-actions">
                  <button
                    onClick={handleSubmitCourse}
                    className="form-btn form-btn-save"
                  >
                    💾 Save Course
                  </button>
                  <button
                    onClick={() => setFormVisible(false)}
                    className="form-btn form-btn-cancel"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* ====================================================================== */}
        {/* CONTENT FORM MODAL */}
        {/* ====================================================================== */}
        {contentFormVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-info-group">
                  <div className="modal-header-title">
                    <h3>Add {contentType.charAt(0).toUpperCase() + contentType.slice(1)}</h3>
                  </div>
                </div>
                <div className="modal-actions-group">
                  <button className="modal-close-btn" onClick={() => setContentFormVisible(false)}>
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">
                <div className="summary-item">
                  <label><strong>Title *</strong></label>
                  <input
                    type="text"
                    className="dropdown"
                    value={contentData.title}
                    onChange={(e) => setContentData({...contentData, title: e.target.value})}
                    placeholder={`${contentType} title`}
                  />
                </div>
                <div className="summary-item">
                  <label><strong>Description</strong></label>
                  <textarea
                    className="dropdown"
                    value={contentData.shortDescription}
                    onChange={(e) => setContentData({...contentData, shortDescription: e.target.value})}
                    placeholder="Description..."
                    rows="3"
                  />
                </div>
                {(contentType === 'quiz' || contentType === 'exam') && (
                  <div className="summary-item">
                    <label><strong>Duration (minutes)</strong></label>
                    <input
                      type="number"
                      className="dropdown"
                      value={contentData.duration}
                      onChange={(e) => setContentData({...contentData, duration: e.target.value})}
                      placeholder="e.g., 60"
                    />
                  </div>
                )}

                <div className="form-actions">
                  <button
                    onClick={handleSubmitContent}
                    className="form-btn form-btn-save"
                  >
                    💾 Add Content
                  </button>
                  <button
                    onClick={() => setContentFormVisible(false)}
                    className="form-btn form-btn-cancel"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================== */}
        {/* CATEGORY FORM MODAL */}
        {/* ====================================================================== */}
        {categoryFormVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-info-group">
                  <div className="modal-header-title">
                    <h3>{categoryFormData.categoryId ? 'Edit Category' : 'Create New Category'}</h3>
                  </div>
                </div>
                <div className="modal-actions-group">
                  <button className="modal-close-btn" onClick={() => setCategoryFormVisible(false)}>
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">
                <div className="summary-item">
                  <label><strong>Category Title *</strong></label>
                  <input
                    type="text"
                    className="dropdown"
                    value={categoryFormData.title}
                    onChange={(e) => setCategoryFormData({...categoryFormData, title: e.target.value})}
                    placeholder="e.g., Machine Learning"
                  />
                </div>

                <div className="summary-item">
                  <label><strong>Tags</strong></label>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input
                      type="text"
                      className="dropdown"
                      style={{ flex: 1 }}
                      value={categoryFormData.tagInput}
                      onChange={(e) => setCategoryFormData({...categoryFormData, tagInput: e.target.value})}
                      placeholder="Add a tag..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && categoryFormData.tagInput.trim()) {
                          e.preventDefault();
                          const newTag = categoryFormData.tagInput.trim().toLowerCase();
                          if (!categoryFormData.tags.includes(newTag)) {
                            setCategoryFormData({
                              ...categoryFormData,
                              tags: [...categoryFormData.tags, newTag],
                              tagInput: ''
                            });
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (categoryFormData.tagInput.trim()) {
                          const newTag = categoryFormData.tagInput.trim().toLowerCase();
                          if (!categoryFormData.tags.includes(newTag)) {
                            setCategoryFormData({
                              ...categoryFormData,
                              tags: [...categoryFormData.tags, newTag],
                              tagInput: ''
                            });
                          }
                        }
                      }}
                      className="compact-btn compact-btn-primary"
                      style={{ padding: '8px 16px' }}
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Display tags as chips */}
                  {categoryFormData.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                      {categoryFormData.tags.map((tag, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <span>{tag}</span>
                          <button
                            onClick={() => {
                              setCategoryFormData({
                                ...categoryFormData,
                                tags: categoryFormData.tags.filter((_, i) => i !== idx)
                              });
                            }}
                            style={{
                              background: 'rgba(255,255,255,0.3)',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    onClick={handleSubmitCategory}
                    className="form-btn form-btn-save"
                  >
                    💾 Save Category
                  </button>
                  <button
                    onClick={() => setCategoryFormVisible(false)}
                    className="form-btn form-btn-cancel"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================== */}
        {/* CATEGORY DETAIL MODAL */}
        {/* ====================================================================== */}
        {categoryModalVisible && selectedCategory && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-info-group">
                  <div className="modal-header-title">
                    <h3>🏷️ {selectedCategory.title}</h3>
                  </div>
                </div>
                <div className="modal-actions-group">
                  <button className="modal-close-btn" onClick={() => setCategoryModalVisible(false)}>
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">
                <div className="info-stats">
                  <div className="info-item">
                    <span className="info-icon">🆔</span>
                    <strong>Category ID:</strong> {selectedCategory.categoryId}
                  </div>
                  <div className="info-item">
                    <span className="info-icon">📚</span>
                    <strong>Courses:</strong> {getCourseCountForCategory(selectedCategory.title)}
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🏷️</span>
                    <strong>Tags:</strong> {selectedCategory.tags?.length > 0 ? selectedCategory.tags.join(', ') : 'No tags'}
                  </div>
                </div>

                {/* Category Actions */}
                <div className="modal-course-actions">
                  <button
                    onClick={() => handleEditCategory(selectedCategory)}
                    className="modal-action-btn compact-btn-secondary"
                  >
                    ✏️ Edit Category
                  </button>
                  <button
                    onClick={() => setCategoryAlertVisible(true)}
                    className="modal-action-btn compact-btn-danger"
                  >
                    🗑️ Delete Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================== */}
        {/* DELETE COURSE CONFIRMATION */}
        {/* ====================================================================== */}
        {alertVisible && (
          <CustomAlert
            visible={alertVisible}
            title="Delete Course"
            icon="🗑️"
            message="Are you sure you want to delete this course? This action cannot be undone."
            onConfirm={handleDeleteCourse}
            onCancel={() => setAlertVisible(false)}
          />
        )}


        {/* ====================================================================== */}
        {/* DELETE CATEGORY CONFIRMATION */}
        {/* ====================================================================== */}
        {categoryAlertVisible && (
          <CustomAlert
            visible={categoryAlertVisible}
            title="Delete Category"
            icon="🗑️"
            message={`Are you sure you want to delete the category "${selectedCategory?.title}"? This action cannot be undone and will fail if courses are using this category.`}
            onConfirm={handleDeleteCategory}
            onCancel={() => setCategoryAlertVisible(false)}
          />
        )}

      </div>
    </div>
  );
};

export default AdminCourseManagement;